import React, { FC, useEffect, useState } from 'react';

import { useMutation } from '@apollo/client';
import { Box, Typography } from '@material-ui/core';
import styled from '@material-ui/core/styles/styled';
import { useTranslation } from 'react-i18next';

import AddToList from '../../../../assets/icons/AddToList';
import RemoveFromList from '../../../../assets/icons/RemoveFromList';
import { IconButton } from '../../../../components/UI/IconButton';
import { ADD_VIDEO_TO_WATCHLIST, REMOVE_VIDEO_FROM_WATCHLIST } from '../../../../graphql/videos';
import { colorsPalette } from '../../../../theme';
import { Mutation, VideoInWatchList } from '../../../../types';
import { formatDuration } from '../../../../utils/helpers';
import useStyles from './styles';

interface VideoTitleProps {
  title?: string;
  isXApiPage?: boolean;
  existsInWatchlist?: boolean;
  userId: string;
  videoId: string;
  duration: number;
}

const VideoTitle: FC<VideoTitleProps> = ({
  title,
  isXApiPage,
  existsInWatchlist = false,
  userId,
  videoId,
  duration,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [existsInList, setExistsInList] = useState<boolean>(existsInWatchlist);

  const [addToWatchList, { loading: loadingAdd }] = useMutation<
    Pick<Mutation, 'AddVideoInWatchList'>,
    { input: VideoInWatchList }
  >(ADD_VIDEO_TO_WATCHLIST, {
    onCompleted: () => {
      setExistsInList(true);
    },
    variables: {
      input: {
        user_id: userId,
        video_id: videoId,
      },
    },
  });
  const [deleteFromWatchList, { loading: loadingDelete }] = useMutation<
    Pick<Mutation, 'DeleteVideoFromWatchList'>,
    { input: VideoInWatchList }
  >(REMOVE_VIDEO_FROM_WATCHLIST, {
    onCompleted: () => {
      setExistsInList(false);
    },
    variables: {
      input: {
        user_id: userId,
        video_id: videoId,
      },
    },
  });

  const handleClick = () => {
    if (!loadingAdd && !loadingDelete) {
      if (existsInList) {
        deleteFromWatchList();
      } else {
        addToWatchList();
      }
    }
  };

  useEffect(() => {
    setExistsInList(existsInWatchlist);
  }, [existsInWatchlist]);

  return (
    <Box className={classes.titleContainer}>
      <TitleWrapper>
        <Typography variant="h2" className={classes.title} data-testid="videoTitle-title">
          {title}
        </Typography>
        <Duration data-testid="videoTitle-duration">{formatDuration(duration)}</Duration>
      </TitleWrapper>
      {!isXApiPage && (
        <IconButton
          icon={
            existsInList ? (
              <RemoveFromList className={classes.removeIcon} />
            ) : (
              <AddToList className={classes.removeIcon} />
            )
          }
          data-testid={existsInList ? 'removeFromWatchlistBtn' : 'addToWatchlistBtn'}
          label={t(existsInList ? 'button.removeFromWatchlist' : 'button.addToWatchlist')}
          onClick={handleClick}
          btnClass={classes.btn}
        />
      )}
    </Box>
  );
};

export default VideoTitle;

const TitleWrapper = styled('div')({
  maxWidth: 'calc(100% - 240px)',
});
const Duration = styled('span')({
  display: 'flex',
  color: colorsPalette.violet,
});
